import React, { useState, useEffect, useCallback } from 'react';
import ScaleSelector from './components/ScaleSelector';
import NoteDisplay from './components/NoteDisplay';
import AnswerDisplay from './components/AnswerDisplay';
import useTimer from './hooks/useTimer';

const bassClefScales = {
  "C Major": ["C1", "D", "E1", "F1", "G1", "A1", "B1", "C2", "E2", "F2", "G2", "A2", "B2"],
  "G Major": ["G1", "A1", "B1", "C2", "E2", "F#2", "G2", "A2", "B2", "C1", "D", "E1", "F#1"],
  "D Major": ["B1", "E1", "F#1", "G1", "A1", "B2", "C#2", "D", "E2", "F#2", "G2", "A2", "C#1"],
  "A Major": ["A1", "B2", "C#2", "D", "E2", "F#2", "G#2", "A2", "C#1", "B1", "E1", "F#1", "G#1"],
  "E Major": ["E1", "F#1", "G#1", "A1", "B1", "C#2", "D#", "E2", "F#2", "G#2", "A2", "C#1", "B2"],
  "B Major": ["B2", "C#2", "D#", "E2", "F#2", "G#2", "A#2", "C#1","B1", "E1", "F#1", "G#1", "A#1"],
  "F Major": ["F1", "G1", "A1", "Bb1", "C2", "D", "E2", "F2", "G2", "A2", "C1", "Bb2", "E1"], 
  "Bb Major": ["Bb1", "C2", "D", "Eb2", "F2", "G2", "A2", "C1", "Bb2", "Eb1", "F1", "G1", "A1"],
  "Eb Major": ["Eb1", "F1", "G1", "Ab1", "Bb1", "C2", "D", "Eb2", "F2", "G2", "Ab2", "C1", "Bb2"],
  "Ab Major": ["Ab1", "Bb1", "C2", "Bb2", "Eb2", "F2", "G2", "Ab2", "C1", "Db", "Eb1", "F1", "G1"],
  "Db Major": ["Bb1", "Eb1", "F1", "Gb1", "Ab1", "Bb2", "C2", "Db", "Eb2", "F2", "Gb2", "Ab2", "C1"]
};

const trebleClefScales = {
  "C Major": ["C1", "D1", "E1", "F1", "G1", "A1", "B", "C2", "D2", "E2", "F2", "G2", "A2"],
  "G Major": ["C1", "D1", "E1", "F#1", "G1", "A1", "B", "C2", "D2", "E2", "F#2", "G2", "A2"],
  "D Major": ["C#1", "D1", "E1", "F#1", "G1", "A1", "B", "C#2", "D2", "E2", "F#2", "G2", "A2"],
  "A Major": ["C#1", "D1", "E1", "F#1", "G#1", "A1", "B", "C#2", "D2", "E2", "F#2", "G#2", "A2"],
  "E Major": ["C#1", "D#1", "E1", "F#1", "G#1", "A1", "B", "C#2", "D#2", "E2", "F#2", "G#2", "A2"],
  "B Major": ["C#1", "D#1", "E1", "F#1", "G#1", "A#1", "B", "C#1", "D#2", "E2", "F#2", "G#2", "A#2"],
  "F Major": ["C1", "D1", "E1", "F1", "G1", "A1", "Bb", "C2", "D2", "E2", "F2", "G2", "A2"],
  "Bb Major": ["C1", "D1", "Eb1", "F1", "G1", "A1", "Bb", "C2", "D2", "Eb2", "F2", "G2", "A2"],
  "Eb Major": ["C1", "D1", "Eb1", "F1", "G1", "Ab1", "Bb", "C2", "D2", "Eb2", "F2", "G2", "Ab2"],
  "Ab Major": ["C1", "Db1", "Eb1", "F1", "G1", "Ab1", "Bb", "C2", "Db2", "Eb2", "F2", "G2", "Ab2"],
  "Db Major": ["C1", "Db1", "Eb1", "F1", "Gb1", "Ab1", "Bb", "C2", "Db2", "Eb2", "F2", "Gb2", "Ab2"]
}

const tenorClefScales = {
  "C Major": ["C1", "D1", "E1", "F1", "G1", "A", "B1", "C2", "D2", "E2", "F2", "G2", "B2"],
  "G Major": ["C1", "D1", "E1", "F#1", "G1", "A", "B1", "C2", "D2", "E2", "F#2", "G2", "B2"],
  "D Major": ["C#1", "D1", "E1", "F#1", "G1", "A", "B1", "C#2", "D2", "E2", "F#2", "G2", "B2"],
  "A Major": ["C#1", "D1", "E1", "F#1", "G#1", "A", "B1", "C#2", "D2", "E2", "F#2", "G#2", "B2"],
  "E Major": ["C#1", "D#1", "E1", "F#1", "G#1", "A", "B1", "C#2", "D#2", "E2", "F#2", "G#2", "B2"],
  "B Major": ["C#1", "D#1", "E1", "F#1", "G#1", "A#", "B1", "C#2", "D#2", "E2", "F#2", "G#2", "B2"],
  "F Major": ["C1", "D1", "E1", "F1", "G1", "A", "Bb1", "C2", "D2", "E2", "F2", "G2", "Bb2"],
  "Bb Major": ["C1", "D1", "Eb1", "F1", "G1", "A", "Bb1", "C2", "D2", "Eb2", "F2", "G2", "Bb2"],
  "Eb Major": ["C1", "D1", "Eb1", "F1", "G1", "Ab", "Bb1", "C2", "D2", "Eb2", "F2", "G2", "Bb2"],
  "Ab Major": ["C1", "Db1", "Eb1", "F1", "G1", "Ab", "Bb1", "C2", "Db2", "Eb2", "F2", "G2", "Bb2"],
  "Db Major": ["C1", "Db1", "Eb1", "F1", "Gb1", "Ab", "Bb1", "C2", "Db2", "Eb2", "F2", "Gb2", "Bb2"]
}

const altoClefScales = {
  "C Major": ["C", "D1", "E1", "F1", "G1", "A1", "B1", "D2", "E2", "F2", "G2", "A2", "B2"],
  "G Major": ["C", "D1", "E1", "F#1", "G1", "A1", "B1", "D2", "E2", "F#2", "G2", "A2", "B2"],
  "D Major": ["C#", "D1", "E1", "F#1", "G1", "A1", "B1", "D2", "E2", "F#2", "G2", "A2", "B2"],
  "A Major": ["C#", "D1", "E1", "F#1", "G#1", "A1", "B1", "D2", "E2", "F#2", "G#2", "A2", "B2"],
  "E Major": ["C#", "D#1", "E1", "F#1", "G#1", "A1", "B1", "D#2", "E2", "F#2", "G#2", "A2", "B2"],
  "B Major": ["C#", "D#1", "E1", "F#1", "G#1", "A#1", "B1", "D#2", "E2", "F#2", "G#2", "A#2", "B2"],
  "F Major": ["C", "D1", "E1", "F1", "G1", "A1", "Bb1", "D2", "E2", "F2", "G2", "A2", "Bb2"],
  "Bb Major": ["C", "D1", "Eb1", "F1", "G1", "A1", "Bb1", "D2", "Eb2", "F2", "G2", "A2", "Bb2"],
  "Eb Major": ["C", "D1", "Eb1", "F1", "G1", "Ab1", "Bb1", "D2", "Eb2", "F2", "G2", "Ab2", "Bb2"],
  "Ab Major": ["C", "Db1", "Eb1", "F1", "G1", "Ab1", "Bb1", "Db2", "Eb2", "F2", "G2", "Ab2", "Bb2"],
  "Db Major": ["C", "Db1", "Eb1", "F1", "Gb1", "Ab1", "Bb1", "Db2", "Eb2", "F2", "Gb2", "Ab2", "Bb2"]
}

// Helper to get scales object by clef
  const getScalesByClef = (clef) => {
    switch (clef) {
      case "bass-clef": return bassClefScales;
      case "alto-clef": return altoClefScales;
      case "tenor-clef": return tenorClefScales;
      default: return trebleClefScales;
    }
  };

const getRandomNote = (selectedScaleNotes, lastNote, selectedScale, selectedClef) => {
  if(lastNote && selectedScaleNotes.length > 0){
    selectedScaleNotes = selectedScaleNotes.filter((item) => item !== lastNote);
  }
  if(selectedScaleNotes.length === 0){
    selectedScaleNotes = shuffleArray(getScalesByClef(selectedClef)[selectedScale]);
  } 
  let map = {'note': selectedScaleNotes[0], 'remainingNotes': selectedScaleNotes};
  return map;
};

const shuffleArray = (arr) => {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]]; // Swap elements
  }
  return arr;
};

const App = () => {
  const [selectedScale, setSelectedScale] = useState("C Major");
  const [selectedClef, setSelectedClef] = useState("treble-clef");
  const [selectedScaleNotes, setSelectedScaleNotes] = useState(shuffleArray(getScalesByClef(selectedClef)[selectedScale]));
  const [currentNote, setCurrentNote] = useState(getRandomNote(selectedScaleNotes, null, null, selectedClef).note);
  const [lastNote, setLastNote] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  
  

  const showNote = useCallback(() => {
    const map = getRandomNote(selectedScaleNotes, lastNote, selectedScale, selectedClef);
    setSelectedScaleNotes(map.remainingNotes);
    setCurrentNote(map.note);
    setLastNote(map.note);
    setShowAnswer(false);
  },[lastNote, selectedScaleNotes,selectedScale]);

  const handleScaleSelectionChange = (scale) => {
    setSelectedScale(scale);
    setLastNote(null);
    setSelectedScaleNotes(shuffleArray(getScalesByClef(selectedClef)[scale]));
  };

  const resetTimer = useTimer(() => setShowAnswer(true), 5000);

  useEffect(() => {
    showNote();
    resetTimer();
    // Disable ESLint rule for this line
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScale]);

  const playAgain = () => {
    showNote();
    resetTimer();
  };

  

  

  // When clef changes, reset scale and notes
  const handleClefChange = (clef) => {
    setSelectedClef(clef);
    setSelectedScale("C Major");
    setLastNote(null);
    setSelectedScaleNotes(shuffleArray(getScalesByClef(clef)["C Major"]));
  };

  return (
    <div className="bg-blue-200 text-gray-800 min-h-screen flex flex-col items-center justify-start gap-2">
      <h1 className="text-4xl font-bold mb-8">Music Notes Guessing Game</h1>
      <div className="flex gap-4 mb-6">
        <button
          className={`px-4 py-2 bg-black text-white rounded shadow hover:bg-gray-800 ${selectedClef === "treble-clef" ? "ring-2 ring-blue-500" : ""}`}
          onClick={() => handleClefChange("treble-clef")}
        >
          Treble Clef
        </button>
        <button
          className={`px-4 py-2 bg-black text-white rounded shadow hover:bg-gray-800 ${selectedClef === "bass-clef" ? "ring-2 ring-blue-500" : ""}`}
          onClick={() => handleClefChange("bass-clef")}
        >
          Bass Clef
        </button>
        <button
          className={`px-4 py-2 bg-black text-white rounded shadow hover:bg-gray-800 ${selectedClef === "alto-clef" ? "ring-2 ring-blue-500" : ""}`}
          onClick={() => handleClefChange("alto-clef")}
        >
          Alto Clef
        </button>
        <button
          className={`px-4 py-2 bg-black text-white rounded shadow hover:bg-gray-800 ${selectedClef === "tenor-clef" ? "ring-2 ring-blue-500" : ""}`}
          onClick={() => handleClefChange("tenor-clef")}
        >
          Tenor Clef
        </button>
      </div>
      <div className="container mx-auto px-4">
        <ScaleSelector
          scales={getScalesByClef(selectedClef)}
          selectedScale={selectedScale}
          onSelectScale={handleScaleSelectionChange}
        />
        <NoteDisplay note={currentNote} scale={selectedScale} scaleType={selectedClef} />
        <AnswerDisplay
          showAnswer={showAnswer}
          answer={currentNote}
          playAgain={playAgain}
        />
      </div>
    </div>
  );
};

export default App;